import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  codeBlockPlugin,
  CodeToggle,
  CreateLink,
  headingsPlugin,
  imagePlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { Image } from 'lucide-react';
import { logger } from '../../../utils/logger';
import { supabase } from '../../../lib/supabase';
import { useUser } from '../../../hooks/queries/useAuth';
import { generateUUID } from '../../../lib/utils';
import { useViewport } from '../../../contexts/ViewportContext';

// Import custom styling for the editor
import './markdown-editor.css';

interface MarkdownEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  topicId?: string;
}

/**
 * Helper function to process images before upload (resize if needed)
 */
async function processImage(file: File): Promise<Blob> {
  // Create a URL for the image
  const url = URL.createObjectURL(file);

  // Load the image for processing
  const img = document.createElement('img');

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = url;
  });

  // Resize if needed
  let width = img.width;
  let height = img.height;

  if (width > 1200 || height > 1200) {
    const aspectRatio = width / height;

    if (width > height) {
      width = 1200;
      height = Math.round(width / aspectRatio);
    } else {
      height = 1200;
      width = Math.round(height * aspectRatio);
    }
    // Image resized to width: ${width}, height: ${height}
  }

  // Create canvas for resizing
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  ctx.drawImage(img, 0, 0, width, height);
  URL.revokeObjectURL(url);

  // Convert to blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to convert image'));
      },
      file.type,
      0.9
    );
  });
}

export function MarkdownEditor({
  content,
  onChange,
  placeholder,
  autoFocus,
  topicId,
}: MarkdownEditorProps) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<MDXEditorMethods>(null);
  const { data: user } = useUser();
  const { isMobile } = useViewport();

  // Remove excessive logging on every content change

  // Ensure proper initialization of content
  useEffect(() => {
    if (autoFocus && editorRef.current) {
      setTimeout(() => {
        editorRef.current?.focus();
      }, 100);
    }
  }, [autoFocus]);

  // Image upload handler that works with MDXEditor's imagePlugin
  const imageUploadHandler = useCallback(
    async (file: File): Promise<string> => {
      logger.component('Image upload handler called', { fileName: file.name });
      setIsUploading(true);

      try {
        // Only accept images
        if (!file.type.startsWith('image/')) {
          logger.component('Image upload - invalid file type', { fileType: file.type });
          throw new Error('Please select an image file.');
        }

        // Ensure user is authenticated
        if (!user) {
          logger.component('Image upload - user not authenticated');
          throw new Error('You must be logged in to upload images');
        }

        // Generate a safe file path using the topic ID if available
        const fileExt = file.name.split('.').pop();
        const fileName = `${topicId || 'notes'}/${generateUUID()}.${fileExt}`;
        logger.component('Image upload - generated filename', { fileName });

        // Process the image (resize if needed)
        const imageBlob = await processImage(file);

        logger.component('Image upload - image processed - originalSize', { size: file.size });
        logger.component('Image upload - image processed - newSize', { size: imageBlob.size });

        // Upload to Supabase storage
        logger.component('Image upload - uploading to storage', { fileName });
        const { error: uploadError } = await supabase.storage
          .from('topic-images')
          .upload(fileName, imageBlob, {
            contentType: file.type,
            upsert: false,
          });

        if (uploadError) {
          logger.component('Image upload - upload error', { error: uploadError.message });
          throw uploadError;
        }

        // Get public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from('topic-images').getPublicUrl(fileName);

        logger.component('Image upload - public URL generated', { publicUrl });
        return publicUrl;
      } catch (uploadError) {
        logger.error('Failed to upload image', uploadError);

        if (uploadError instanceof Error) {
          logger.component('Image upload - error details', { message: uploadError.message });
          alert(`Upload failed: ${uploadError.message}`);
        } else {
          logger.component('Image upload - unknown error');
          alert('Failed to upload image. Please try again later.');
        }

        throw uploadError;
      } finally {
        setIsUploading(false);
      }
    },
    [topicId, user]
  );

  // Custom file input handler
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setIsUploading(true);
      const file = files[0];

      // Validate and process using the same logic
      const publicUrl = await imageUploadHandler(file);

      // Instead of directly appending to content string, we'll use the editor's
      // built-in methods to modify the content, which will also automatically
      // render the image and synchronize with our content state
      if (editorRef.current) {
        // Get current position in editor or go to end
        // Get current content
        const currentContent = editorRef.current.getMarkdown();

        // Create markdown and insert at the end of content (with proper line breaks)
        const imageMarkdown = `\n\n![${file.name}](${publicUrl})`;
        const newContent = currentContent + imageMarkdown;

        // Update editor content and trigger change
        editorRef.current.setMarkdown(newContent);

        // The editor's onChange will be triggered by setMarkdown,
        // which will call our handleEditorChange method
      } else {
        // Fallback if editor ref isn't available (shouldn't happen)
        const imageMarkdown = `![${file.name}](${publicUrl})`;
        const newContent = content ? `${content}\n\n${imageMarkdown}` : imageMarkdown;
        onChange(newContent);
      }

      logger.component('Image upload via button - completed successfully');
    } catch (error) {
      logger.error('Failed to upload image', error);
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setIsUploading(false);
    }
  };

  // Handle editor changes
  const handleEditorChange = useCallback(
    (newContent: string) => {
      // Notify parent about the change
      onChange(newContent);
    },
    [onChange]
  );

  // Memoize plugins to prevent unnecessary re-renders
  const editorPlugins = useMemo(
    () => [
      // Core plugins for document structure
      headingsPlugin({
        allowedHeadingLevels: [1, 2, 3], // Limit to H1, H2, H3
      }),
      listsPlugin(),
      markdownShortcutPlugin(),
      quotePlugin(),
      thematicBreakPlugin(),

      // Basic content plugins
      linkPlugin(),
      linkDialogPlugin(),
      codeBlockPlugin(), // Include code blocks but remove from toolbar

      // Image handling with integrated uploader
      imagePlugin({
        imageUploadHandler,
        disableImageResize: false,
        imageSettingsButtonOptions: {
          visible: false,
        },
        imageDeleteButtonOptions: {
          visible: false,
        },
      }),

      // No source editing - removed diffSourcePlugin

      // Toolbar plugin with responsive layout
      toolbarPlugin({
        toolbarContents: () => (
          <div className="flex items-center justify-between w-full">
            {/* Left toolbar items in a single row */}
            <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto">
              {/* Text formatting - always has border */}
              <div className="flex-shrink-0 flex items-center border-r border-gray-200 pr-2 sm:pr-4">
                <BoldItalicUnderlineToggles />
              </div>

              {/* Block type select dropdown - keep on all devices */}
              <div
                className={`flex-shrink-0 flex items-center ${isMobile ? '' : 'border-r border-gray-200 pr-4'}`}
              >
                <BlockTypeSelect className="w-28 md:w-auto" />
              </div>

              {isMobile ? (
                // Mobile layout - simplified components
                <>
                  {/* Lists toggle with only bullet and number options */}
                  <div className="flex-shrink-0 flex items-center">
                    <ListsToggle options={['bullet', 'number']} />
                  </div>

                  {/* Action buttons positioned at right side */}
                  <div
                    className="flex items-center absolute right-1"
                    style={{ top: '0', height: '100%' }}
                  >
                    {/* Direct link button - simplified structure */}
                    <div className="flex-shrink-0 flex items-center">
                      <CreateLink />
                    </div>

                    {/* Direct image upload button with tooltip */}
                    <div className="flex-shrink-0 flex items-center ml-1">
                      <button
                        type="button"
                        className="p-1 rounded text-gray-700 transition-colors cursor-pointer"
                        title="Upload image"
                        aria-label="Upload image"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Image size={16} className={isUploading ? 'animate-pulse' : ''} />
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept="image/*"
                          className="hidden"
                        />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // Desktop layout - standard components
                <>
                  {/* Lists - same configuration for consistency */}
                  <div className="flex-shrink-0 flex items-center border-r border-gray-200 pr-4">
                    <ListsToggle options={['bullet', 'number']} />
                  </div>

                  {/* Links */}
                  <div className="flex-shrink-0 flex items-center border-r border-gray-200 pr-4">
                    <CreateLink />
                  </div>

                  {/* Code toggle button - desktop only */}
                  <div className="flex-shrink-0 flex items-center border-r border-gray-200 pr-4">
                    <CodeToggle />
                  </div>

                  {/* Direct image upload button */}
                  <div className="flex-shrink-0 flex items-center">
                    <button
                      type="button"
                      className="p-1.5 rounded text-gray-700 transition-colors cursor-pointer"
                      title="Upload image"
                      aria-label="Upload image"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Image size={16} className={isUploading ? 'animate-pulse' : ''} />
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/*"
                        className="hidden"
                      />
                    </button>
                  </div>

                  {/* Right side - just undo/redo - only on desktop */}
                  <div className="absolute right-2 flex items-center gap-2">
                    <UndoRedo />
                  </div>
                </>
              )}
            </div>
          </div>
        ),
      }),
    ],
    [imageUploadHandler, isUploading, handleFileSelect]
  );

  // Return just the editor with a wrapper div to help with CSS
  return (
    <div className="mdxeditor-wrapper">
      <MDXEditor
        ref={editorRef}
        markdown={content || ''}
        onChange={handleEditorChange}
        placeholder={placeholder || 'Start typing your note here...'}
        autoFocus={autoFocus}
        contentEditableClassName="prose prose-sm max-w-none min-h-64 font-montserrat focus:outline-none p-4 w-full"
        plugins={editorPlugins}
      />
    </div>
  );
}
